"use client";

import React from "react";
import { PhoneCall, ShieldCheck, Clock, MapPin, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const highlights = [{
  label: "24/7 Rapid Emergency Response"
}, {
  label: "Licensed & Insured Master Plumbers"
}, {
  label: "Transparent Upfront Pricing"
}, {
  label: "Residential & Commercial Service"
}];
const quickStats = [{
  label: "Average Response Time",
  value: "< 45 Mins"
}, {
  label: "Forest Park Dispatch",
  value: "24/7 / 365"
}, {
  label: "Workmanship Warranty",
  value: "100% Guaranteed"
}];
export default function ForestPark() {
  return <section id="heroforestpark" data-nav="dark" className="relative overflow-hidden bg-primary py-16 text-primary-foreground md:py-24 lg:py-28">
      {/* Decorative background radial pattern */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,var(--color-accent)/0.12,transparent_60%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,var(--color-primary-foreground)/0.04,transparent_50%)]" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Copy and CTAs */}
          <div className="flex flex-col items-start lg:col-span-7">
            {/* Location & Status Badge */}
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1.5 text-xs font-semibold text-accent shadow-xs sm:text-sm">
              <MapPin className="size-4 shrink-0 text-accent" />
              <span>Forest Park, GA & Clayton County</span>
              <span className="inline-block size-1.5 rounded-full bg-accent" />
              <span className="font-medium text-primary-foreground/90">24/7 Immediate Dispatch</span>
            </div>

            {/* Primary Heading */}
            <h1 className="font-default text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl lg:text-5xl leading-[1.15]">
              Plumbing Services in <span className="text-accent underline decoration-accent/40 underline-offset-4">Forest Park, GA</span>
            </h1>

            {/* Subheading */}
            <p className="mt-5 max-w-2xl text-base text-primary-foreground/85 sm:text-lg md:text-xl leading-relaxed">
              Professional Plumbing Help Available 24/7 in Forest Park. From severe pipe bursts and stubborn drain clogs to full water heater replacements, our local plumbers arrive fast with fully-equipped trucks.
            </p>

            {/* Feature Bullets */}
            <div className="mt-6 grid w-full grid-cols-1 gap-2.5 sm:grid-cols-2">
              {highlights.map((item, i) => <div key={item.label} data-index={i} className="flex items-center gap-2 text-sm text-primary-foreground/90">
                  <CheckCircle2 className="size-4 shrink-0 text-accent" />
                  <span>{item.label}</span>
                </div>)}
            </div>

            {/* Call to Actions */}
            <div className="mt-8 flex w-full flex-col gap-3.5 sm:flex-row sm:items-center">
              <a href="tel:4708479076" className="w-full sm:w-auto">
                <Button size="lg" className="w-full bg-accent font-semibold text-accent-foreground shadow-md hover:bg-accent/90 sm:w-auto">
                  <PhoneCall className="size-5" />
                  <span>Call (470) 847-9076</span>
                </Button>
              </a>

              <Link to="/contact/" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="w-full border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground backdrop-blur-xs hover:bg-primary-foreground/20 hover:text-primary-foreground sm:w-auto">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>

            {/* Trust Footer Bar */}
            <div className="mt-10 flex flex-wrap items-center gap-6 border-t border-primary-foreground/15 pt-6 text-xs text-primary-foreground/80 sm:text-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-accent" />
                <span>Zero Hidden Trip Fees</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="size-4 text-accent" />
                <span>On-Site Within Minutes</span>
              </div>
            </div>
          </div>

          {/* Right Column: Visual Imagery and Local Service Card */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {/* Outer decorative card frame */}
              <div className="relative overflow-hidden rounded-2xl border border-primary-foreground/20 bg-card p-3 shadow-2xl">
                
                {/* Hero Image */}
                <div className="relative h-72 w-full overflow-hidden rounded-xl sm:h-80 md:h-96">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/forest_park_plumbing_service.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/forest_park_plumbing_service.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/forest_park_plumbing_service.png-wvc-sizes" alt="Licensed emergency plumber servicing pipes in Forest Park GA" loading="lazy" className="size-full object-cover object-center" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
                  
                  {/* Image Overlay Badge */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-lg bg-primary/90 p-3 backdrop-blur-xs text-primary-foreground">
                    <div>
                      <p className="text-xs font-semibold text-accent uppercase tracking-wider">Forest Park Team</p>
                      <p className="text-sm font-bold">Jonesboro Emergency Plumber</p>
                    </div>
                    <Badge variant="outline" className="border-accent/40 bg-accent/20 text-accent">
                      Active On Duty
                    </Badge>
                  </div>
                </div>

                {/* Quick Stats Grid under Image */}
                <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl bg-muted/60 p-3 text-card-foreground">
                  {quickStats.map((stat, i) => <div key={stat.label} data-index={i} className="text-center">
                      <p className="font-mono text-xs font-bold text-foreground sm:text-sm">
                        {stat.value}
                      </p>
                      <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground sm:text-xs">
                        {stat.label}
                      </p>
                    </div>)}
                </div>

              </div>

              {/* Floating Emergency Badge */}
              <div className="absolute -bottom-4 -left-3 hidden rounded-xl border border-accent/40 bg-card p-3.5 shadow-xl sm:flex sm:items-center sm:gap-3 text-card-foreground">
                <div className="flex size-10 items-center justify-center rounded-lg bg-accent text-accent-foreground font-bold">
                  <PhoneCall className="size-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Immediate Assistance</p>
                  <p className="font-mono text-sm font-bold text-foreground">(470) 847-9076</p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}